import React, { useEffect, useRef, useState } from "react";
import {
  GetUnansweredQuestion,
  GetUserAgentChats,
  GetUserAgentConnections,
  UpdateChatConnectionsIsSaved,
  AnswerUnansweredQuestion,
} from "../../Apis_chatbot/apis";
import { ChatRoles } from "../../app/Enums/ChatRoles";
import "./User_Agent1.css";
import Pagination from "../../app/components/Pagination/Pagination";
import {
  GetQaFromUserAgentChat,
  saveQaPairs,
  addQAPoint,
} from "../../Api_Qdrant/Flask_Apis";
import { toast } from "react-toastify";
import Linkify from "linkify-react";
import * as linkify from "linkifyjs";
import agentProfileSvg from "../../assets/icons/agent-profile.svg";
import userProfileSvg from "../../assets/icons/user-profile.svg"

export default function UserAgentConversation() {
  const [connections, setConnections] = useState([]);
  const [isGeneratedFilter, setIsGeneratedFilter] = useState(false);
  const [chats, setChats] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [chatLog, setChatLog] = useState("");
  const [unansweredQuestion, setUnansweredQuestion] = useState(null);
  const [isAnswerBoxOpen, setIsAnswerBoxOpen] = useState(false);
  const [generatedQaFromChat, setGeneratedQaFromChat] = useState([]);
  const [showAddAnsBtn, setShowAddAnsBtn] = useState(true);
  const chatEndRef = useRef(null);

  // QA form states
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Loader & Error state for Generate QA button
  const [loadingConnectionId, setLoadingConnectionId] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [inputValue, setInputValue] = useState("");
   const tableRef = useRef(null);

  const LinkifyProperties = {
    target: "_blank",
    rel: "noopener noreferrer",
    className: "chat-link",
  };

  useEffect(() => {
    setShowAddAnsBtn(true);
  }, [selectedConnectionId]);

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchChats();
  }, [currentPage, pageSize, isGeneratedFilter]);

  // ===== Fetch connections with pagination =====
  const fetchConnections = async () => {
    try {
      const response = await GetUserAgentConnections({
        pageNo: currentPage,
        pageSize,
        isGenerated: isGeneratedFilter,
      });

      setConnections(response.result_value || []);
      if (response.total_row) {
        setTotalPages(Math.ceil(response.total_row / pageSize));
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
      toast.error("Unable to load conversations");
    }
  };

  // ===== Fetch chats and unanswered question for a connection =====
  const fetchUnansweredQuestion = async (connectionId) => {
    try {
      const response = await GetUnansweredQuestion({ connectionId });
      if (response) setUnansweredQuestion(response.result_value);
    } catch (error) {
      console.error("Error fetching unanswered question:", error);
      // No toast needed here - it's okay if there's no unanswered question
    }
  };

  const fetchChats = async (connectionId) => {
    try {
      const response = await GetUserAgentChats({ connectionId });
      if (response) setChats(response.result_value);
    } catch (error) {
      console.error("Error fetching chat:", error);
      toast.error("Could not load chat messages");
    }
  };

  const generateChatLog = () => {
    if (!chats || chats.length === 0) {
      toast.error("No chat messages available");
      return;
    }

    if (!unansweredQuestion?.question) {
      toast.error("No question found to generate answers");
      return;
    }

    const formattedChats = chats.map((chat) => {
      const senderRoleName = ChatRoles[chat.senderRoleId] || "Unknown";
      return `${senderRoleName.toLowerCase()}: ${chat.message}`;
    });

    GenerateQAfromChats(formattedChats.join("\n"));
  };

  const GenerateQAfromChats = async (chats) => {
    if (!chats || !unansweredQuestion?.question) {
      return;
    }

    setLoadingConnectionId(selectedConnectionId);
    setGenerateError(null);

    try {
      const response = await GetQaFromUserAgentChat(
        chats,
        unansweredQuestion.question
      );

      if (response.success === false) {
        toast.error("Failed to generate Q&A from chat");
        setGenerateError(
          "Could not generate Q&A. The chat may not have enough content."
        );
        return;
      }

      if (response.success === true && response.result) {
        const qaPairs =
          response.result.qa_pairs || response.result.qa_results || [];
        const totalQa = response.result.total_qa_generated || qaPairs.length;

        if (totalQa > 0) {
          setGeneratedQaFromChat(qaPairs);
          setShowAddAnsBtn(false);
          toast.success(`Generated ${totalQa} Q&A pairs successfully`);
        } else {
          setGenerateError("No Q&A pairs could be generated");
          toast.info(
            "The chat didn't contain enough information to generate Q&A"
          );
        }
      } else {
        setGenerateError("Unexpected response format");
        toast.error("Failed to process chat content");
      }
    } catch (error) {
      console.error("Error generating Q&A:", error);

      let userMessage = "Failed to analyze chat";

      if (error.response) {
        const status = error.response.status;

        if (status === 429) {
          userMessage = "Too many requests. Please wait a moment.";
        } else if (status === 503) {
          userMessage = "AI service is busy. Please try again.";
        } else if (status === 502 || status === 504) {
          userMessage = "Service temporarily unavailable";
        } else if (status === 500) {
          userMessage = "Server error. Please try again.";
        } else if (status >= 400 && status < 500) {
          userMessage = "Could not process this chat";
        }
      } else if (error.message?.includes("Network Error")) {
        userMessage = "Check your internet connection";
      }

      setGenerateError(userMessage);
      toast.error(userMessage);
    } finally {
      setLoadingConnectionId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Ongoing";
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSelectConnection = (connectionId) => {
    setSelectedConnectionId(connectionId);
    fetchChats(connectionId);
    fetchUnansweredQuestion(connectionId);
    setGeneratedQaFromChat([]);
    setGenerateError(null);
  };

  const selectedConnection = connections.find(
      (c) => c.id === selectedConnectionId
  );
  const headerText =
  selectedConnection?.userName?.trim() ||
  selectedConnection?.connectionId ||
  "Select a conversation";
  const shouldShowProfile = headerText !== "Select a conversation";


  const handleEditQA = (index, updatedQuestion, updatedAnswer) => {
    const updatedQA = [...generatedQaFromChat];
    updatedQA[index] = { question: updatedQuestion, answer: updatedAnswer };
    setGeneratedQaFromChat(updatedQA);
  };

  const handleDeleteQA = (index) => {
    setGeneratedQaFromChat((prev) => prev.filter((_, i) => i !== index));
    toast.info("Question deleted");
  };

  const handleAddQA = () => {
    if (!newQuestion.trim()) {
      toast.warning("Please enter a question");
      return;
    }

    if (!newAnswer.trim()) {
      toast.warning("Please enter an answer");
      return;
    }

    setGeneratedQaFromChat((prev) => [
      ...prev,
      { question: newQuestion, answer: newAnswer },
    ]);
    setNewQuestion("");
    setNewAnswer("");
    toast.success("New Q&A pair added");
  };

  const handleFinalSubmit = async () => {
    if (generatedQaFromChat.length === 0) {
      toast.warning("No Q&A pairs to submit");
      return;
    }

    try {
      const response = await saveQaPairs("CustomAi", generatedQaFromChat);
      if (response) {
        toast.success(`Submitted ${generatedQaFromChat.length} Q&A pairs`);
      } else {
        toast.error("Failed to save Q&A pairs");
      }

      const res = await UpdateChatConnectionsIsSaved({
        conn_Id: selectedConnectionId,
      });
      if (res) {
        toast.success("Conversation marked as processed");

        await fetchConnections();

        setGeneratedQaFromChat([]);
        setShowAddAnsBtn(true);
        setUnansweredQuestion(null);
      } else {
        toast.error("Could not update conversation status");
      }
    } catch (error) {
      console.error("Submit error:", error);

      let userMessage = "Failed to submit Q&A";

      if (error.response) {
        const status = error.response.status;
        if (status === 429) userMessage = "Too many submissions. Please wait.";
        else if (status >= 500) userMessage = "Server error. Please try again.";
        else if (status >= 400) userMessage = "Invalid data format.";
      } else if (error.message?.includes("Network")) {
        userMessage = "Network error. Please check connection.";
      }

      toast.error(userMessage);
    }
  };

  const handleAddAnswer = async () => {
    if (!inputValue.trim()) {
      toast.warning("Please enter an answer");
      return;
    }

    try {
      const response = await addQAPoint(
        "CustomAi",
        unansweredQuestion.question,
        inputValue
      );
      if (response) {
        toast.success("Answer submitted successfully");
      } else {
        toast.error("Failed to submit answer");
      }

      const result = await AnswerUnansweredQuestion({
        questionId: unansweredQuestion.questionId,
        adminId: localStorage.getItem("UserId"),
        answer: inputValue,
      });

      if (result.result_value === true) {
        toast.success("Answer recorded");

        await fetchConnections();
        await fetchUnansweredQuestion(selectedConnectionId);
        await fetchChats(selectedConnectionId);

        setInputValue("");
        setIsAnswerBoxOpen(false);
      }

      const res = await UpdateChatConnectionsIsSaved({
        conn_Id: selectedConnectionId,
      });
      if (res) {
        toast.success("Conversation updated");
        await fetchConnections();
      }
    } catch (error) {
      console.error("Error adding answer:", error);

      let userMessage = "Failed to add answer";

      if (error.response) {
        const status = error.response.status;
        if (status === 429) userMessage = "Too many attempts. Please wait.";
        else if (status >= 500) userMessage = "Server error. Please try again.";
      } else if (error.message?.includes("Network")) {
        userMessage = "Network error. Please check connection.";
      }

      toast.error(userMessage);
    }
  };

    useEffect(() => {
      tableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, [pageSize, currentPage]);

  return (
    <>
      <div className="role-box">
        <h2>User-Agent Conversations</h2>
        <div className="search-filter-section">
          <div className="form-wraper">
            <label htmlFor="isGeneratedFilter" className="filter-label">
              Filter:
            </label>
            <select
              id="isGeneratedFilter"
              value={isGeneratedFilter}
              onChange={(e) => {
                setIsGeneratedFilter(e.target.value === "true");
                setCurrentPage(1);
              }}
            >
              <option value="false">Not Processed</option>
              <option value="true">Processed</option>
            </select>
          </div>
          <div>

          </div>

        </div>
      </div>
      <div className="user-agent-container">
        {/* HEADER */}
        {unansweredQuestion && (
          <div className="user-agent-header">

            <div className="unanswered-box">
              <p>
                <strong>Question:</strong> {unansweredQuestion.question}
              </p>
              {showAddAnsBtn && !isGeneratedFilter && (
                <div>
                  <button
                    className="add-answer-btn"
                    onClick={() => setIsAnswerBoxOpen(!isAnswerBoxOpen)}
                  >
                    {isAnswerBoxOpen ? "Close" : "Add Answer"}
                  </button>

                  {isAnswerBoxOpen && (
                    <div className="answer-box">
                      <input
                        type="text"
                        placeholder="Write your answer..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                      />
                      <button className="submit-btn" onClick={handleAddAnswer}>
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* MAIN LAYOUT */}
        <main className="user-agent-main">
          {/* SIDEBAR */}
          <div className="sidebar">
            <aside className="user-agent-sidebar">
              <div className="agent-header">
                <h2>Conversations</h2>
              </div>
              <div className="agent-body" ref={tableRef}>
                {connections.length === 0 ? (
                  <p className="no-connections">No conversations found</p>
                ) : (
                  connections.map((conn, index) => (
                    <div
                      key={conn.id}
                      className={`sidebar-item ${selectedConnectionId === conn.id ? "active" : ""
                        }`}
                      onClick={() => handleSelectConnection(conn.id)}
                    >


                      <div className="sidebar-details">
                        <div className="agent-converation-bx">
                          <img src={agentProfileSvg} alt="Profile" />
                          <div className="agent-profile-text">
                            <span className="sidebar-connection">
                              <strong>Connection:</strong> {conn.connectionId}
                            </span>
                            <p>
                              agent : {conn.agentName}
                              <br></br>
                              user : {conn.userName}
                            </p>
                          </div>
                        </div>
                      
                      <div className="agent-date-btn-wraper">
                        <p>
                          <strong>Started:</strong> {formatDate(conn.startedAt)}
                        </p>
                        <p>
                          <strong>Ended:</strong> {formatDate(conn.endedAt)}
                        </p>

                        <p className="qa-status">
                          {conn.isQaGenerated ? (
                            <span className="qa-yes">
                              <strong>Status:</strong> Processed
                            </span>
                          ) : (
                            <div className="qa-actions">
                              <button
                                className="primary_btn"
                                disabled={loadingConnectionId === conn.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedConnectionId(conn.id);
                                  generateChatLog();
                                }}
                              >
                                {loadingConnectionId === conn.id
                                  ? "Processing..."
                                  : "Generate Q&A"}
                              </button>
                            </div>
                          )}
                        </p>
                        </div>

                      </div>
                    </div>
                  ))
                )}
              </div>
            </aside>
            <div className="sidebar-footer">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
            </div>
          </div>

          {/* CHAT SECTION */}
          <section className="user-agent-chat">
            <div className="agent-header d-flex align-items-center">
                {shouldShowProfile && (
                    <img src={agentProfileSvg} alt="Profile" />
                  )}
                <h2>{headerText}</h2>
              </div>
              <div className="agent-chat-box">
            {selectedConnectionId ? (
              chats.length > 0 ? (
                chats.map((chat, idx) => (
                  <div
                    key={idx}
                    className={`chat-message-wrapper ${chat.senderRoleId === 1 ? "right" : " left"
                      }`}
                  >
                    <div
                      className={`chat-message ${chat.senderRoleId === 1 ? "agent-msg" : "user-msg"
                        }`}
                    >
                      <img src={chat.senderRoleId === 1 ? userProfileSvg : agentProfileSvg} alt="Profile" />
                      <div>
                        <span className="time">
                          {new Date(chat.sendedAt).toLocaleTimeString()}
                        </span>
                        {/* <h5>{chat.senderRoleId === 1 ? "Agent" : "User"}</h5> */}
                        <Linkify className="chat-message-text" options={LinkifyProperties}>
                          {chat.message}
                        </Linkify>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-chats-message">
                  No messages in this conversation
                </p>
              )
            ) : (
              <p className="select-conversation-message">
                Select a conversation to view chat
              </p>
            )}
            </div>
            <div ref={chatEndRef}></div>
          </section>
        </main>

        {/* GENERATED Q&A SECTION */}
        {generatedQaFromChat.length > 0 && (
          <section className="generated-qa-section">
            <div className="qa-section-header">
              <h3 className="qa-section-title">
                📝 Generated Q&A ({generatedQaFromChat.length})
              </h3>
              <button className="submit-btn-final" onClick={handleFinalSubmit}>
                ✅ Save All
              </button>
            </div>

            <div className="qa-list">
              {generatedQaFromChat.map((qa, index) => (
                <div key={index} className="qa-item">
                  <p className="qa-question">
                    <strong>Q{index + 1}:</strong>{" "}
                    <input
                      value={qa.question}
                      onChange={(e) =>
                        handleEditQA(index, e.target.value, qa.answer)
                      }
                      disabled={index === 0}
                      placeholder="Edit question..."
                    />
                  </p>
                  <p className="qa-answer">
                    <strong>A:</strong>{" "}
                    <input
                      value={qa.answer}
                      onChange={(e) =>
                        handleEditQA(index, qa.question, e.target.value)
                      }
                      placeholder="Edit answer..."
                    />
                  </p>
                  {index > 0 && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteQA(index)}
                      title="Delete this Q&A pair"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add New QA */}
            <div className="qa-add-form">
              <h4>➕ Add New Q&A</h4>
              <div className="qa-add-inputs">
                <input
                  type="text"
                  placeholder="Enter new question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Enter answer"
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                />
                <button
                  onClick={handleAddQA}
                  disabled={!newQuestion.trim() || !newAnswer.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
